import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, Check, Loader2, Video, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/context/AuthContext";

/* "Notify me when someone is available" — with mode-specific subscriptions.

   Single source of truth for the waitlist CTA. Dropped into both the
   landing Hero and the authed Homepage; behaviour depends only on
   `useAuth().status`:

     - unauth → clicking stashes a pending-action flag in sessionStorage
       and routes to /login. On successful OAuth return the component
       automatically drains the flag + POSTs the registration (both modes)
       so the signed-in user is NOT asked again to click the button.
     - auth + not registered → clicking opens a small mode picker: Camera
       On, Camera Off, or Both. Choosing one POSTs /waitlist/notify-me with
       those modes and flips the button to the confirmed state.
     - auth + already registered → shows a static "✓ You'll be emailed"
       pill with the subscribed modes, no interaction.

   Props:
     - returnTo   : path to come back to after Google OAuth (defaults to
                    the current pathname so the user lands exactly where
                    they clicked)
     - variant    : "primary" (red filled, used in the landing hero) or
                    "ghost"  (bordered, used on the Homepage empty state)
     - testid     : override the default data-testid
*/

const PENDING_KEY = "salespit_pending_action";

const MODES = [
  { key: "both", label: "Both", modes: ["video", "phone"], Icon: BellRing },
  { key: "video", label: "Camera On", modes: ["video"], Icon: Video },
  { key: "phone", label: "Camera Off", modes: ["phone"], Icon: Phone },
];

function modeLabel(modes) {
  if (!modes || modes.length === 2) return "Camera On & Off";
  return modes[0] === "phone" ? "Camera Off" : "Camera On";
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors";
const PRIMARY_ACTIVE =
  "bg-[#E52525] text-white shadow-[0_0_24px_rgba(229,37,37,0.3)] hover:bg-[#FF3333] active:scale-95";
const GHOST_ACTIVE =
  "border border-[#E52525]/40 bg-[#E52525]/10 text-white hover:bg-[#E52525]/20";
const CONFIRMED =
  "border border-[#22C55E]/30 bg-[#22C55E]/10 text-[#22C55E] cursor-default";
const LOADING_STYLE =
  "border border-white/10 bg-transparent text-zinc-400 cursor-default";

export function NotifyMeButton({
  returnTo,
  variant = "primary",
  testid = "notify-me-btn",
}) {
  const navigate = useNavigate();
  const { status } = useAuth();
  const [state, setState] = useState("loading"); // loading|idle|picking|sending|registered
  const [modes, setModes] = useState(null); // subscribed modes, null = both
  const wrapRef = useRef(null);

  // Resolve registration status whenever auth state changes. Also drain any
  // pending "notify-me" flag the user set BEFORE being bounced to login so
  // they don't have to click twice after signing in.
  useEffect(() => {
    if (status === "initializing") return;

    if (status !== "authenticated") {
      // Unauth → always the "clickable" state (we don't call the backend).
      setState("idle");
      return;
    }

    let alive = true;
    (async () => {
      const pending = sessionStorage.getItem(PENDING_KEY) === "notify-me";
      if (pending) sessionStorage.removeItem(PENDING_KEY);
      try {
        if (pending) {
          // The user already asked to be notified before signing in — honour
          // that intent silently (both modes) and show the confirmed state.
          setState("sending");
          await api.post("/waitlist/notify-me");
          if (!alive) return;
          toast.success("You're on the list — we'll email you when a rep is live.");
          setModes(null);
          setState("registered");
          return;
        }
        const r = await api.get("/waitlist/notify-me");
        if (!alive) return;
        setModes(r.data?.modes || null);
        setState(r.data?.registered ? "registered" : "idle");
      } catch {
        if (alive) setState("idle");
      }
    })();
    return () => {
      alive = false;
    };
  }, [status]);

  // Close the mode picker on outside click.
  useEffect(() => {
    if (state !== "picking") return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setState("idle");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [state]);

  const handleClick = async () => {
    if (state !== "idle") return;

    // Unauth → stash intent and bounce to login.
    if (status !== "authenticated") {
      try {
        sessionStorage.setItem(PENDING_KEY, "notify-me");
      } catch {
        /* storage disabled — the user will just have to click again */
      }
      navigate("/login", {
        state: { from: returnTo || window.location.pathname },
      });
      return;
    }

    // Auth → open the mode picker.
    setState("picking");
  };

  const pickMode = async (choice) => {
    setState("sending");
    try {
      const r = await api.post("/waitlist/notify-me", { modes: choice.modes });
      setModes(r.data?.modes || null);
      setState("registered");
      toast.success(
        `You're on the list — we'll email you when a ${modeLabel(choice.modes)} rep is live.`
      );
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Couldn't register — try again.";
      toast.error(msg);
      setState("idle");
    }
  };

  if (state === "loading") {
    return (
      <span
        data-testid={`${testid}-loading`}
        className={`${BASE} ${LOADING_STYLE}`}
      >
        <Loader2 size={14} className="animate-spin" /> Checking…
      </span>
    );
  }

  if (state === "registered") {
    return (
      <span data-testid={`${testid}-registered`} className={`${BASE} ${CONFIRMED}`}>
        <Check size={14} /> You'll be emailed · {modeLabel(modes)}
      </span>
    );
  }

  if (state === "sending") {
    return (
      <span
        data-testid={`${testid}-sending`}
        className={`${BASE} ${LOADING_STYLE}`}
      >
        <Loader2 size={14} className="animate-spin" /> Saving…
      </span>
    );
  }

  const activeStyle = variant === "ghost" ? GHOST_ACTIVE : PRIMARY_ACTIVE;
  const Icon = variant === "ghost" ? Bell : BellRing;

  return (
    <span ref={wrapRef} className="relative inline-flex" data-testid={`${testid}-wrap`}>
      <button
        type="button"
        data-testid={testid}
        aria-expanded={state === "picking"}
        onClick={handleClick}
        className={`${BASE} ${activeStyle}`}
      >
        <Icon size={15} />
        Notify me when someone is available
      </button>
      {state === "picking" && (
        <span
          data-testid={`${testid}-mode-picker`}
          className="absolute left-1/2 top-full z-30 mt-2 flex -translate-x-1/2 gap-1.5 rounded-full border border-white/10 bg-[#141414] p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
        >
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              data-testid={`${testid}-mode-${m.key}`}
              onClick={() => pickMode(m)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-[#E52525] hover:text-white"
            >
              <m.Icon size={13} />
              {m.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

export default NotifyMeButton;
